/**
 * Client-project ("matter") CRUD copy, base (legal) dialect. The en catalog is
 * complete by definition - its object type IS the key schema every other
 * locale overrides. Sourced from the matter create/edit dialog (`MatterForm`),
 * the matter picker (`MatterSelector`) and the hub's matters card and switcher
 * (`routes/hub`) - all of which speak of "matters"; the business dialect
 * overrides them to "projects".
 */
export const project = {
    form: {
        /** Dialog title when creating a new matter. */
        addTitle: 'Add Matter',
        /** Dialog title when editing an existing matter. */
        editTitle: 'Edit Matter',
        /** Name field label (required). */
        nameLabel: 'Matter name *',
        /** Name field example placeholder. */
        namePlaceholder: 'Nkosi v Dlamini Litigation',
        /** Custom-reference field example placeholder. */
        customReferencePlaceholder: 'Smith Estate / ABC/2024/001',
        /** Description field placeholder. */
        descriptionPlaceholder: 'Brief description of the matter',
        /** Zod validation message: name field left blank. */
        nameRequired: 'Matter name is required',
        /** Submit button when creating. */
        createSubmit: 'Create matter',
        /** Submit button when updating. */
        updateSubmit: 'Save changes',
        deleteButton: 'Delete matter',
        /** Inline error fallback when the create mutation fails without a server message. */
        createError: 'Failed to create matter. Please try again.',
        /** Inline error fallback when the update mutation fails without a server message. */
        updateError: 'Failed to update matter. Please try again.',
    },
    summary: {
        /** Section divider label above the name/reference/description fields on the edit form. */
        sectionHeading: 'Matter',
    },
    /**
     * A fee earner's own hourly rate on a client or a matter, and the shared rate
     * behind it (`components/fee-earner-rate/`). Only the strings that name the
     * fee earner or the matter live here; the rest are the same in every dialect.
     */
    rate: {
        clientSummaryLabel: 'Your client rate',
        namedClientSummaryLabel: '{{feeEarner}} client rate',
        namedProjectSummaryLabel: '{{feeEarner}} matter rate',
        projectSummaryLabel: 'Your matter rate',
        /** Label of the picker an owner or assistant uses to choose whose rate the field edits. */
        feeEarnerLabel: 'Fee earner',
        rowsLabel: 'Fee-earner rates',
        addRowLabel: 'Add fee-earner rate',
        removeRowLabel: 'Remove rate for {{name}}',
        /** Confirm body for removing a client's shared rate; interpolates {{rate}} and {{name}}. */
        removeSharedFromClient: 'Remove the shared rate of {{rate}} from {{name}}? Fee earners without their own rate on this client then bill at their Team rate. Matters that have their own shared rate keep it.',
        /** Confirm body for removing a matter's shared rate; interpolates {{rate}} and {{name}}. */
        removeSharedFromProject: "Remove the shared rate of {{rate}} from {{name}}? Fee earners with their own rate on this matter or its client keep billing at it. Everyone else then bills at the client's shared rate if it has one, otherwise at their Team rate.",
        /**
         * Line under a matter's rate field when the fee earner has no rate on the
         * matter and bills at their own rate on its client. The same in every dialect.
         */
        ownClientRateApplies: 'Client rate: {{rate}}',
    },
    selector: {
        /** Search input placeholder (both inline and dropdown variants). */
        searchPlaceholder: 'Search matters...',
        /** Accessible label for the search input. */
        searchAriaLabel: 'Search matters',
        /** Empty state when the account has no active matters at all. */
        noActiveProjects: 'No active matters',
        /** Error state when the matter list fails to load. */
        couldNotLoad: 'Could not load matters',
        /** Accessible label for the grouped results listbox. */
        listboxAriaLabel: 'Matters',
        /** Accessible label for the clear (X) button on a committed selection. */
        clearSelectionAriaLabel: 'Clear matter selection',
    },
    list: {
        /** Heading of the hub's Matters card. */
        heading: 'Matters',
        /** Button that opens the create-matter dialog. */
        addButton: 'Add matter',
        /** Button shown inside the empty state. */
        addFirstButton: 'Add first matter',
        /** Empty state when a client has no matters; interpolates {{clientName}}. */
        emptyStateForClient: 'No matters for {{clientName}}',
        /** Matter table column header. */
        columnHeader: 'Matter',
        /** Placeholder for the search box above the hub's expanded matters list. */
        searchPlaceholder: 'Search matters...',
        /** Accessible label for that search box. */
        searchAriaLabel: 'Search matters',
        /** Shown in place of the table when a search matches none of the client's matters (distinct from `emptyStateForClient`, which is a client with no matters at all). */
        noSearchResults: 'No matters match your search',
        noFilterResults: 'No matters match your filters',
    },
    detail: {
        /** Loading message on the hub's matter view. */
        loading: 'Loading matter...',
        /** Error alert when the matter fails to load. */
        loadError: "Couldn't load this matter",
    },
    /**
     * MatterMatchChips (shared client + matter match editor behind the emails
     * and calendar inline allocation UI - `components/matter/MatterMatchChips.tsx`).
     */
    match: {
        /** Dashed "add" chip shown when no matter is matched yet. */
        addChipLabel: '+ Matter',
        /** Accessible label for the unmatched add-matter chip trigger. */
        addAriaLabel: 'Match matter',
        /** Accessible label for the matched chip trigger; interpolates {{name}}. */
        changeAriaLabel: 'Change matched matter: {{name}}',
        /** Accessible label for the × button that clears a matched matter. */
        removeAriaLabel: 'Remove matter',
        /** SearchPopover empty state when the picked client has no matching matters. */
        noResultsForClient: 'No matters found for this client',
        /** Modal-variant link that reopens the picker for an already-matched activity. */
        reallocate: 'Reallocate to a different matter',
    },
    /**
     * Auto-suggestion reason sentences (`components/matter/autoSuggestionReason.ts`),
     * shown in the AutoSuggestionLabel tooltip on the emails and calendar lists.
     * Email forms are lowercase-initial clauses appended after a client
     * headline; event forms are standalone capitalised sentences.
     */
    matchReason: {
        referenceInEmail: 'Matter reference appears in the email',
        nameInEmail: 'Matter name appears in the email',
        threadInherited: 'An earlier email in this thread is allocated to this matter',
        referenceClauseInEmail: 'matter reference appears in the email',
        nameClauseInEmail: 'matter name appears in the email',
        fuzzyClauseInEmail: 'the email closely matches this matter',
        singleProjectClauseInEmail: 'this client has a single active matter',
        similarEmailsFiled: 'Emails like this one were filed to this client before',
        recentMatterClauseInEmail: 'the matter this client was most recently filed under',
        historyVoteClauseInEmail: 'similar emails were filed to this matter before',
        referenceInEvent: 'Matter reference appears in the event',
        nameInEvent: 'Matter name appears in the event',
        fuzzyInEvent: 'Event details closely match this matter',
        singleProjectInEvent: 'This client has a single active matter',
    },
    /**
     * Shared "matter is archived" warning banner (calendar event / email inline
     * detail panels) and its matching calendar/email match-status headline.
     */
    archiveBanner: {
        heading: 'Matter is archived',
        bodyForEmail: "The email stays linked for audit, but you can't log time against this matter until it's restored.",
        bodyForEvent: "The event stays linked for audit, but you can't log time against this matter until it's restored.",
    },
    /** No-matter-yet hint on the calendar/email inline detail activity zone. */
    noMatterHint: {
        forEmail: 'Pick a matter to log activity for this email.',
        forEvent: 'Pick a matter to log activity for this event.',
    },
    /** Reject-draft ConfirmDialog message (calendar/email inline detail). */
    rejectDialog: {
        emailMessage: 'Archives the draft. The email stays linked to this matter for audit - use the unlink action above to clear the link too.',
        eventMessage: 'The draft will be archived. The event stays linked to this matter for audit - use the unlink action above to clear the link too.',
    },
    /**
     * getCalendarMatchStatusMessage / getMatchStatusMessage
     * (`routes/calendar.match-status.ts` / `routes/emails.match-status.ts`) -
     * only the matter-naming messages route through copy; dialect-neutral
     * headlines/explanations stay literal in the helper.
     */
    matchStatus: {
        draftDeletedForEvent: 'The draft was removed. This event is still linked to the matter for audit. Remove the allocation to clear the link too.',
        draftDeletedForEmail: 'The draft was removed. This email is still linked to the matter for audit. Remove the allocation to clear the link too.',
        noMatterSelectedHeadline: 'No matter selected',
        noMatterSelectedForEvent: 'This event is linked to a client but not yet to a matter. Choose a matter to log time.',
        noMatterSelectedForEmail: 'This email is linked to a client but not yet to a matter. Choose a matter to log time.',
        noMatterMatchedHeadline: 'No matter matched',
        noMatterMatchedExplanation: 'The matcher could not find a matter for this event. Choose one above and log a draft if it represents billable work.',
        lowConfidenceExplanationForEvent: 'The matter match was too uncertain to log automatically. Check the allocation and log a draft if it looks right.',
    },
    /** Calendar activity-status pill/hint copy (`routes/calendar.helpers.ts`). */
    calendarActivityStatus: {
        noMatterSelected: 'No draft - no matter selected',
        matterMatchNeedsReview: 'No draft - matter match needs review',
    },
    /** ActivityStatePill "matter archived" state (`components/activity/ActivityStatePill.tsx`). */
    pill: {
        archivedLabel: 'Matter archived',
        archivedTitle: 'The matter is archived. This entry stays linked for audit.',
    },
    /** Calendar/emails/activities "+ Filter" bar matter filter (shared faceted filter-bar pattern, `apps/web/bill/CLAUDE.md` "Apply UI improvements to neighbouring pages"). */
    filter: {
        label: 'Matter',
        ariaLabel: 'matter filter',
        allOption: 'All matters',
        clearUnmatchedHint: 'Clear Unmatched to filter by matter',
    },
    /** Activities page permanent search box placeholder (`routes/activities.tsx`). */
    activitySearchPlaceholder: 'Search by description, matter or client',
    activitySearchPlaceholderInClient: 'Search by description or matter',
    activitySearchPlaceholderInMatter: 'Search by description',
    /** GenerateReportDialog matter multi-filter (`components/activity/GenerateReportDialog.tsx`). */
    reportFilter: {
        /** Hint shown once matters are selected, explaining the client filter is gated off. */
        clearHint: 'Clear matters to filter by client.',
        /** Toast error when the matter search fails to load. */
        loadError: 'Could not load matters. Please try again.',
        /** Toast error when hydrating the selected matter labels fails. */
        resolveError: 'Could not resolve selected matter labels. Please try again.',
    },
    /**
     * Expense-to-matter attribution (SupplierInvoiceForm, BankLineExpenseCreatePanel,
     * expense-detail.helpers.ts deriveMatterLockState).
     */
    expenseAttribution: {
        sectionHeading: 'Matter',
        /** Pinned first option of the matter picker in the expense Edit details dialog; moves the expense back to a business cost. */
        noMatterOption: 'No matter (business cost)',
        /** Toast after the matter of an approved or paid expense is corrected. */
        matterChangedToast: 'Matter changed',
        /** Save is held while the matter picker is cleared and empty. */
        chooseMatterReason: 'Choose a matter, or choose no matter.',
        hint: "Link this expense to a matter so it appears in that client's billable expenses when creating an invoice.",
        /**
         * SupplierInvoiceForm - caption under the disabled matter picker on an
         * approved or paid expense. The matter changes through Edit details, which
         * reposts the journal.
         */
        lockedPosted: 'This expense is approved. To change its matter, choose Edit details.',
        lockedBilled: 'This expense is billed to a client, so its matter is locked.',
    },
    /** Delete-client confirm dialog body (`routes/client-form/ClientFormPage.tsx`); interpolates {{name}}. */
    clientDeleteDialog: {
        message: "Delete {{name}}? Their matters are deleted too. They stay on past invoices but won't appear when adding new ones.",
        /**
         * Tooltip on the disabled "Delete client" control while the client holds a
         * live section 86(4) trust investment account. Plural on the
         * blocking-account count; {{accounts}} is their labels, comma-joined.
         */
        blockedReason_one: 'Has an open trust investment account: {{accounts}}',
        blockedReason_other: 'Has open trust investment accounts: {{accounts}}',
    },
    deleteDialog: {
        message: 'Delete {{name}}? Associated activities and documents are archived. Past invoices and financial records are retained.',
    },
    /**
     * CSV bulk-import template headers and entity labels
     * (`components/bulk-import/csv-templates.ts`). Header-matching in
     * `csv-transforms.ts` accepts both dialects' headers on import regardless
     * of which one generated the template, so these strings only affect the
     * downloaded template and the on-screen entity labels, not parsing.
     */
    csv: {
        /** "Matter Name*" template column header (matters template). */
        nameHeaderRequired: 'Matter Name*',
        /** "Matter Reference*" template column header (activities template, required). */
        referenceHeaderRequired: 'Matter Reference*',
        /** "Matter Reference" template column header (invoices template, optional). */
        referenceHeaderOptional: 'Matter Reference',
    },
};
